import { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { CustomButton } from '@medtronic/pecc-react-component-library-js';
import { Box, Modal } from '@mui/material';
import { ClusterQuery } from '~/api/v2/endpoints/cluster';
import { useCreateClusterMutation } from '~/api/v2/hooks';
import { ModalHeader } from '~/components';
import { modalActionBlockStyles, modalContainerStyle } from '~/components/styles';
import { useAppDispatch } from '~/redux';
import { closeModalById } from '~/redux/slices/modalSlice';
import { clusterSchema } from '~/validation';
import CreateEmrClusterForm from './CreateEmrClusterForm';

export interface CreateEmrClusterModalProps {
  payload: {
    id: string;
    projectName: string;
    emrId: number;
  };
}

const CreateEmrClusterModal: FC<CreateEmrClusterModalProps> = ({ payload: { id, projectName, emrId } }) => {
  const projectId = +useParams().projectid!;
  const dispatch = useAppDispatch();
  const [createCluster] = useCreateClusterMutation({
    fixedCacheKey: `create-cluster-emr-${emrId}`,
  });

  const formMethods = useForm<Omit<ClusterQuery, 'projectId' | 'emrId'>>({
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(clusterSchema),
    defaultValues: {
      name: '',
      primary_ec2_instance_type: '',
      core_ec2_instance_type: '',
      ebs_size: undefined,
      ebs_iops: undefined,
      ebs_throughput: undefined,
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isValid },
  } = formMethods;

  const handleClose = () => {
    reset();
    dispatch(closeModalById(id));
  };

  const onSubmit = (data: Omit<ClusterQuery, 'projectId' | 'emrId'>) => {
    createCluster({ projectId, emrId, ...data });
    handleClose();
  };

  return (
    <Modal open onClose={handleClose}>
      <Box sx={modalContainerStyle}>
        <ModalHeader onClose={handleClose} title={['Create Cluster', `New Cluster for project “${projectName}“`]} />
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <CreateEmrClusterForm />
            <Box sx={modalActionBlockStyles}>
              <CustomButton buttonType="borderless" label="Cancel" onClick={handleClose} />
              <CustomButton label="Create" type="submit" disabled={!isValid} />
            </Box>
          </form>
        </FormProvider>
      </Box>
    </Modal>
  );
};

export default CreateEmrClusterModal;
